<template>
  <div class="klarna-return">
    <!-- Priority Physician Review — offered once Klarna's payment is confirmed and
         the order fulfilled, and charged separately to a card entered in the modal:
         Klarna authorizes one fixed amount up front and leaves nothing to bill.
         Either answer continues to the same confirmation page. -->
    <fast-track-upsell-modal
      v-if="fastTrackOfferOpen && fastTrackAddon"
      :addon="fastTrackAddon"
      payment-method="klarna"
      :base-url="baseUrl"
      :user-id="(user && user.id) || ''"
      :first-name="(user && user.first_name) || ''"
      :last-name="(user && user.last_name) || ''"
      :email="(user && user.email) || ''"
      :shipping-address="fastTrackShippingAddress"
      :tracking-session-id="trackingSessionId"
      @close="closeFastTrackOffer"
    />

    <div class="klarna-return-content">
      <!-- Loading. Held through the add-on offer so a confirmed order never shows
           an empty screen behind the modal. -->
      <div v-if="loading || fastTrackOfferOpen" class="klarna-loading">
        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 100 100">
          <circle cx="50" cy="50" fill="none" stroke="#00ffc8" stroke-width="10" r="35" stroke-dasharray="165 57">
            <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.5s" values="0 50 50;360 50 50" keyTimes="0;1" />
          </circle>
        </svg>
        <p class="klarna-status-text">Confirming your payment...</p>
      </div>

      <!-- Pending — Klarna approved but has not settled yet -->
      <div v-if="pending" class="klarna-pending">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#00ffc8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        <h2>Payment Being Confirmed</h2>
        <p>Klarna has approved your payment and it is still settling. Nothing else is needed from you. We'll email you as soon as your order is confirmed.</p>
      </div>

      <!-- Error -->
      <div v-if="error" class="klarna-error">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h2>Payment Not Completed</h2>
        <p>{{ errorMessage }}</p>
        <button class="klarna-retry-btn" @click="goBackToCheckout">
          Return to Checkout <span>&rarr;</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import { mapGetters } from 'vuex';
import FastTrackUpsellModal from './FastTrackUpsellModal.vue';
import { loadFastTrackAddon } from '@/utils/fastTrackUpsell';
import { fireOrderPurchase } from '@/utils/orderPurchaseTracking';

/** Longest a confirmed order waits on the add-on catalog before skipping the offer. */
const ADDON_DECISION_MAX_MS = 2500;

/** Each Klarna return route, mapped to the checkout route that started the payment. */
const CHECKOUT_FOR_RETURN_ROUTE = {
  KlarnaReturnCallCenter: 'checkoutCallCenter',
  KlarnaReturnWeightLoss: 'checkoutWeightLoss',
  KlarnaReturnCheckoutFlow: 'CheckoutFlowNew',
};

export default {
  name: 'KlarnaReturn',
  components: {
    FastTrackUpsellModal,
  },
  data() {
    return {
      loading: true,
      pending: false,
      error: false,
      errorMessage: '',
      baseUrl: process.env.VUE_APP_API_ENDPOINT,
      // Priority Physician Review. Resolved while Klarna's confirmation is in
      // flight, then offered once the order is fulfilled. null (product missing or
      // inactive) means no offer is ever made.
      fastTrackAddon: null,
      fastTrackOfferOpen: false,
      // The redirect this page would have made immediately if there were no offer.
      fastTrackPendingRoute: null,
      trackingSessionId: '',
      // The in-flight catalog resolution, awaited (bounded) at decision time.
      addonRequest: null,
    };
  },
  computed: {
    ...mapGetters(['user', 'shipping']),

    // The store keeps the saved address in the API's snake_case shape; the charge
    // wants the checkout shape. Empty values are fine — the backend bills against
    // the customer's stored Stripe address, this is only carried for the record.
    fastTrackShippingAddress() {
      const shipping = this.shipping || {};
      return {
        addressLine1: shipping.address_line_1 || '',
        addressLine2: shipping.address_line_2 || '',
        city: shipping.city || '',
        state: shipping.state || '',
        zip: shipping.zip || '',
      };
    },
  },
  async mounted() {
    this.trackingSessionId = (typeof window !== 'undefined' && window.trackingSessionId) || '';
    // Started, not awaited: this runs alongside the Klarna confirmation rather than
    // in front of it. This page is a fresh page load, so App.vue's catalog fetch may
    // still be in flight and the resolution can genuinely need a request of its own.
    this.addonRequest = loadFastTrackAddon(this.baseUrl);
    this.addonRequest.then((resolved) => { this.fastTrackAddon = resolved; });
    await this.confirmPayment();
  },
  methods: {
    // Offers Priority Physician Review if the catalog has it, otherwise routes
    // straight on. The redirect is stashed rather than dropped: whichever way the
    // customer answers, they land on the same confirmation with the same values.
    async offerFastTrackOrRoute(route) {
      // The catalog request has been running since mount; give its tail a bounded
      // moment rather than dropping the offer on a slow connection. A stalled
      // request resolves to no offer instead of parking a paid customer here.
      if (!this.fastTrackAddon && this.addonRequest) {
        this.fastTrackAddon = await Promise.race([
          this.addonRequest,
          new Promise((resolve) => setTimeout(() => resolve(null), ADDON_DECISION_MAX_MS)),
        ]);
      }

      if (!this.fastTrackAddon) {
        this.$router.push(route);
        return;
      }
      this.fastTrackPendingRoute = route;
      this.fastTrackOfferOpen = true;
    },

    closeFastTrackOffer() {
      this.fastTrackOfferOpen = false;
      const pending = this.fastTrackPendingRoute;
      this.fastTrackPendingRoute = null;
      if (pending) {
        this.$router.push(pending);
      }
    },

    async confirmPayment() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const clientSecret = urlParams.get('payment_intent_client_secret');
        const paymentIntentId = urlParams.get('payment_intent');

        if (!clientSecret || !paymentIntentId) {
          this.error = true;
          this.errorMessage = 'Missing payment information. Please try again.';
          this.loading = false;
          return;
        }

        // Check PaymentIntent status via Stripe.js
        const stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);

        if (paymentIntent.status === 'succeeded') {
          // Attach the promo cart snapshot stashed by checkoutCallCenter.vue before the Klarna
          // redirect. Single-use and matched to THIS payment intent — every other checkout that
          // shares this page writes no snapshot, so they keep sending { paymentIntentId } only.
          let klarnaCartItems = [];
          try {
            const snap = JSON.parse(sessionStorage.getItem('pending_klarna_cart') || 'null');
            if (snap && snap.payment_intent_id === paymentIntentId && Array.isArray(snap.items)) {
              klarnaCartItems = snap.items;
            }
          } catch (e) {
            klarnaCartItems = [];
          }
          sessionStorage.removeItem('pending_klarna_cart');

          // Confirm on backend and fulfill order
          const confirmBody = { paymentIntentId: paymentIntentId };
          if (klarnaCartItems.length > 0) confirmBody.cartItems = klarnaCartItems;
          const response = await this.$axios.post(`${this.baseUrl}/api/confirm-klarna-payment`, confirmBody);

          if (response.data.status === 'success') {
            await this.$store.dispatch('setPrescriptionPreauths', response.data.prescriptionPreauths);

            // What Stripe authorized, which is what the customer paid. The coupon is NOT
            // taken off again here: all three checkouts create the intent net of it, so
            // subtracting response.data.discount reported the order short by the discount.
            const amountCharged = paymentIntent.amount / 100;
            const transactionIdentifier = response.data.transaction_id ||
              (response.data.prescriptionPreauths?.[0]?.transactionId) ||
              `${response.data.customerId}_${Date.now()}`;

            // Klarna orders have never reported a purchase: this page routes to
            // verifyPhoneAndEmail, which has no tracking and then sends the customer to
            // the homepage, so the thanks page that fired the event was never reached.
            fireOrderPurchase({
              amount: amountCharged,
              transactionId: transactionIdentifier,
            });

            // Card checkout reports the call-center conversion inline; Klarna redirects away,
            // so replay the stashed conversion here before leaving the page.
            await this.postCallConversion(paymentIntentId);

            await this.offerFastTrackOrRoute({
              name: 'verifyPhoneAndEmail',
              query: {
                terms: String(response.data.payment_terms || 1),
                amount: amountCharged,
                trans_id: transactionIdentifier,
              },
            });
          } else {
            this.error = true;
            this.errorMessage = response.data.message || 'Order fulfillment failed. Please contact support.';
          }
        } else if (paymentIntent.status === 'processing') {
          // Klarna settles asynchronously, so `processing` is a normal intermediate state,
          // NOT a failure — the customer has approved and Stripe will move the intent to
          // succeeded on its own. Do not post /api/confirm-klarna-payment here: the payment
          // has not succeeded yet. The backend's payment_intent.succeeded webhook creates
          // the order when it settles, so there is nothing for this page to do but say so.
          // Before this branch existed, `processing` fell through to the generic else below
          // and told a paying customer to contact support.
          this.pending = true;
        } else if (paymentIntent.status === 'requires_payment_method') {
          this.error = true;
          this.errorMessage = 'Payment was not approved. Please try again with a different payment method.';
        } else {
          this.error = true;
          this.errorMessage = `Payment status: ${paymentIntent.status}. Please contact support if you were charged.`;
        }
      } catch (error) {
        console.error('Klarna confirmation error:', error);
        this.error = true;
        this.errorMessage = error.response?.data?.message || 'Failed to confirm payment. Please contact support.';
      } finally {
        this.loading = false;
      }
    },
    // Replays the call-center conversion that checkoutCallCenter.vue stashed before the
    // Klarna redirect. No-op for every other checkout that shares this return page
    // (only the call-center flow writes `pending_call_conversion`). Mirrors the card
    // flow's sendConversionTracking: posts /api/call-conversion with 3 retries.
    async postCallConversion(paymentIntentId, attempt = 1) {
      let pending;
      try {
        pending = JSON.parse(sessionStorage.getItem('pending_call_conversion') || 'null');
      } catch (e) {
        pending = null;
      }
      if (!pending || !pending.c_id) return;

      // Guard against a stale/abandoned attempt posting the wrong sale: the stashed
      // payment intent must match the one we just confirmed.
      if (pending.payment_intent_id !== paymentIntentId) {
        sessionStorage.removeItem('pending_call_conversion');
        return;
      }

      try {
        const res = await this.$axios.post(`${this.baseUrl}/api/call-conversion`, {
          user_id: pending.user_id,
          user_name: pending.user_name,
          product_name: pending.product_name,
          amount: pending.amount,
          c_id: pending.c_id,
          payment_terms: pending.payment_terms,
          // Rep attribution stashed by checkoutCallCenter.vue before the redirect.
          // `?? null` keeps a stash written by an older build (no rep keys) posting cleanly.
          sa_id: pending.sa_id ?? null,
          team_id: pending.team_id ?? null,
          v_agent: pending.v_agent ?? null,
        }, { timeout: 10000 });

        if (res.data && res.data.status === 'success') {
          sessionStorage.removeItem('pending_call_conversion');
          sessionStorage.removeItem('affiliate_conversion_id');
        } else if (attempt < 3) {
          setTimeout(() => this.postCallConversion(paymentIntentId, attempt + 1), 2000);
        }
      } catch (error) {
        console.error(`Call conversion post error (attempt ${attempt}):`, error);
        if (attempt < 3) {
          setTimeout(() => this.postCallConversion(paymentIntentId, attempt + 1), Math.pow(2, attempt) * 1000);
        }
      }
    },
    // Back to the checkout this Klarna payment started from, keeping the rep params
    // checkoutCallCenter.vue put on the return_url and dropping Stripe's redirect params.
    goBackToCheckout() {
      const query = { ...this.$route.query };
      delete query.payment_intent;
      delete query.payment_intent_client_secret;
      delete query.redirect_status;
      this.$router.push({
        name: CHECKOUT_FOR_RETURN_ROUTE[this.$route.name],
        params: this.$route.params,
        query,
      });
    },
  },
};
</script>

<style scoped>
.klarna-return {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.klarna-return-content {
  text-align: center;
  padding: 40px;
  max-width: 480px;
}

.klarna-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.klarna-status-text {
  font-size: 18px;
  color: #374151;
  font-weight: 500;
}

.klarna-pending,
.klarna-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.klarna-pending h2 {
  font-size: 22px;
  color: #1f2937;
  margin: 0;
}

.klarna-pending p {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.klarna-error h2 {
  font-size: 22px;
  color: #1f2937;
  margin: 0;
}

.klarna-error p {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.klarna-retry-btn {
  margin-top: 8px;
  padding: 14px 32px;
  background: var(--dark-bluegreen, #0b3b3c);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.klarna-retry-btn:hover {
  opacity: 0.9;
}
</style>
